import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LocationAutocomplete from "../../components/LocationAutocomplete";
import CustomerEnquiries from "../../layout/builders/CustomerEnquiry/CustomerEnquiries";
import PostedHouses from "../../layout/realestateagent/HousingPost/PostedHouses";
import Layout from "../../layout/builders/Layout";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import toast from "react-hot-toast";
import RichTextEditor from "../../components/common/RichTextEditor";
import DurationSelectionModal from "../../layout/realestateagent/HousingPost/DurationSelectionModal";
import LocationPicker from "../../components/LocationPicker";
import {
  BuildingOffice2Icon,
  HomeModernIcon,
  HomeIcon,
  MapPinIcon,
  BanknotesIcon,
  UserGroupIcon,
  SparklesIcon,
  ArrowsPointingOutIcon,
  TruckIcon,
  PhotoIcon,
  ShieldCheckIcon,
  BoltIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  Squares2X2Icon,
  CheckCircleIcon,
  ChevronDownIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

const TOTAL_STEPS = 5;

const GradientHeader = ({ step, totalSteps }) => (
  <div className="rounded-3xl bg-gradient-to-r from-blue-50 via-sky-100 to-blue-100 px-5 py-4">
    <div className="flex items-center justify-between gap-3">
      <div className="flex-1">
        <p className="text-xs uppercase tracking-wide text-blue-700">
          Step {step} of {totalSteps}
        </p>
        <div className="mt-1 h-1.5 w-full bg-white/60 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>
      </div>
      <div className="hidden sm:block text-right text-[11px] text-blue-700">
      </div>
    </div>
  </div>
);

const StepCard = ({ stepNumber, title, description, children, isOpen, toggle }) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-md hover:border-blue-100 hover:-translate-y-[1px]">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4"
      >
        <div className="text-left">
          <h2 className="text-sm sm:text-base font-semibold text-gray-900">
            {title}
          </h2>
          {description && (
            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
          )}
        </div>
        <ChevronDownIcon
          className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${isOpen ? "rotate-180" : ""
            }`}
        />
      </button>
      <div
        className={`border-t border-gray-100 overflow-hidden transition-all duration-300 ${isOpen ? "max-h-[1200px] opacity-100" : "max-h-0 opacity-0"
          }`}
      >
        <div className="px-4 py-4 sm:px-5 sm:py-5">{children}</div>
      </div>
    </div>
  );
};

const PROPERTY_TYPES = [
  { id: "Apartment", label: "Apartment", icon: BuildingOffice2Icon },
  { id: "Villa", label: "Villa", icon: HomeModernIcon },
  { id: "Flat", label: "Flat", icon: HomeIcon },
  { id: "Independent House", label: "Independent House", icon: HomeIcon },
  { id: "Studio", label: "Studio", icon: HomeModernIcon },
  { id: "Row House", label: "Row House", icon: BuildingOffice2Icon },
  { id: "Duplex", label: "Duplex", icon: BuildingOffice2Icon },
  { id: "Penthouse", label: "Penthouse", icon: HomeModernIcon },
  { id: "Farm House", label: "Farm House", icon: HomeIcon }
];

const LISTING_TYPES = ["Sale", "Rent", "Lease"];
const TRANSACTION_TYPES = ["New", "Resale", "Under Construction"];
const FURNISHING = ["Fully Furnished", "Semi Furnished", "Unfurnished"];
const FACING = ["East", "West", "North", "South", "NE", "NW", "SE", "SW"];
const PARKING = ["No Parking", "1 Parking", "2 Parking"];

const AMENITIES = [
  "Lift",
  "CCTV",
  "Security",
  "24x7 Water Supply",
  "Power Backup",
  "Gym",
  "Swimming Pool",
  "Clubhouse",
  "Kids Play Area",
  "Garden",
];

const STEPS_META = [
  {
    id: 1,
    label: "Basics",
    title: "Tell us about your place",
    desc: "Start with the basic details of your property.",
  },
  {
    id: 2,
    label: "Location",
    title: "Property Location",
    desc: "Share accurate city and address information.",
  },
  {
    id: 3,
    label: "Structure",
    title: "Property Structure",
    desc: "Describe the layout and core specifications.",
  },
  {
    id: 4,
    label: "Media & Amenities",
    title: "Photos & Facilities",
    desc: "Add amenities and photos to stand out.",
  },
  {
    id: 5,
    label: "Contact",
    title: "Contact Information",
    desc: "Let buyers know how to reach you.",
  },
];

const HousingPlot = ({ listingMode, editBasePath }) => {
  const [activeTab, setActiveTab] = useState("post");
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    // Basic
    propertyTitle: "",
    listingType: listingMode || "",
    propertyType: "",
    transactionType: "",
    expectedPrice: "",
    priceNegotiable: "Yes",
    // Location
    city: "",
    locality: "",
    streetAddress: "",
    landmark: "",
    pincode: "",
    mapUrl: "",
    latitude: null,
    longitude: null,
    // Specs
    plotArea: "",
    builtupArea: "",
    bedrooms: "",
    bathrooms: "",
    balconies: "",
    furnishing: "",
    floorNumber: "",
    totalFloors: "",
    facing: "",
    carParking: "",
    // Amenities
    amenities: [],
    // Additional
    description: "",
    highlights: "",
    nearbyLocations: "",
    // Contact
    ownerName: "",
    mobileNumber: "",
    altNumber: "",
    email: "",
    preferredContactTime: "",
  });

  const [images, setImages] = useState([]); // [{file, previewUrl}]
  const [errors, setErrors] = useState({});
  const [openSections, setOpenSections] = useState({
    1: true,
    2: true,
    3: true,
    4: true,
    5: true,
  });
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [createdListingId, setCreatedListingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const navigate = useNavigate();

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const toggleAmenity = (amenity) => {
    setForm((prev) => {
      const set = new Set(prev.amenities);
      if (set.has(amenity)) set.delete(amenity);
      else set.add(amenity);
      return { ...prev, amenities: Array.from(set) };
    });
  };

  const handleImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const validFiles = [];
    files.forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`Image ${file.name} exceeds 10MB limit.`);
        return;
      }
      validFiles.push(file);
    });

    if (validFiles.length === 0) return;

    const mapped = validFiles.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setImages((prev) => [...prev, ...mapped]);
  };

  const removeMedia = (index) => {
    const itemToRemove = images[index];
    if (itemToRemove && itemToRemove.existing && itemToRemove.imageId) {
      setDeletedImageIds((prev) => [...prev, itemToRemove.imageId]);
    }
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const setAsMain = (index) => {
    if (index === 0) return;
    setImages((prev) => {
      const newImages = [...prev];
      const selected = newImages.splice(index, 1)[0];
      newImages.unshift(selected);
      return newImages;
    });
    // Scroll to top of preview if needed, but usually it's fine
  };

  const validateStep = () => {
    const newErrors = {};

    if (step === 1) {
      if (!form.propertyType) newErrors.propertyType = "Select a property type";
      if (!form.listingType) newErrors.listingType = "Choose listing type";
      if (!form.propertyTitle.trim())
        newErrors.propertyTitle = "Property title is required";
      if (!form.expectedPrice)
        newErrors.expectedPrice = "Expected price is required";
    }

    if (step === 2) {
      if (!form.city.trim()) newErrors.city = "City is required";
      if (!form.locality.trim())
        newErrors.locality = "Locality / Area is required";
      if (!form.streetAddress.trim())
        newErrors.streetAddress = "Street address is required";
    }

    if (step === 3) {
      if (!form.plotArea) newErrors.plotArea = "Plot area is required";
      if (!form.bedrooms) newErrors.bedrooms = "Bedrooms required";
      if (!form.bathrooms) newErrors.bathrooms = "Bathrooms required";
    }

    if (step === 5) {
      if (!form.ownerName.trim())
        newErrors.ownerName = "Owner name is required";
      if (!form.mobileNumber.trim())
        newErrors.mobileNumber = "Mobile number is required";
      if (!form.email.trim()) newErrors.email = "Email is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const { id } = useParams();
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (id) {
      handleEdit({ id });
    }
  }, [id]);

  const handleEdit = async (listing) => {
    try {
      setSubmitting(true);
      const response = await axiosInstancev1.get(`/properties/${listing.id}`);
      const data = response.data;

      if (data.success) {
        const details = data.details || {};

        // Parse amenities
        let amenities = details.amenities;
        if (typeof amenities === 'string') {
          try { amenities = JSON.parse(amenities); } catch (e) { amenities = []; }
        } else if (!Array.isArray(amenities)) {
          amenities = [];
        }

        setForm({
          // Basic
          propertyTitle: details.property_title || details.plot_title || "",
          listingType: listingMode || (data.listing_type ? data.listing_type.charAt(0).toUpperCase() + data.listing_type.slice(1).toLowerCase() : ""),
          propertyType: PROPERTY_TYPES.some(pt => pt.id === (details.property_type || "")) ? details.property_type : "",
          transactionType: details.transaction_type || "",
          expectedPrice: details.expected_price || details.price || "",
          priceNegotiable: details.is_price_negotiable ? "Yes" : "No",
          // Location
          city: data.city || "",
          locality: data.locality || "",
          streetAddress: data.street_address || "",
          landmark: data.landmark || "",
          pincode: data.pincode || "",
          mapUrl: data.google_map_link || "",
          latitude: data.latitude || null,
          longitude: data.longitude || null,
          // Specs
          plotArea: details.plot_area || "",
          builtupArea: details.built_up_area || "",
          bedrooms: details.bedrooms || "",
          bathrooms: details.bathrooms || "",
          balconies: details.balconies || "",
          furnishing: details.furnishing_status || "",
          floorNumber: details.floor_number || "",
          totalFloors: details.total_floors || "",
          facing: details.facing_direction || "",
          carParking: details.car_parking_slots ? `${details.car_parking_slots} Parking` : "No Parking",
          // Amenities
          amenities: amenities,
          // Additional
          description: details.description || "",
          highlights: details.highlights || "",
          nearbyLocations: details.nearby_locations || "",
          // Contact
          ownerName: data.contact_name || "",
          mobileNumber: data.contact_mobile || "",
          altNumber: data.contact_alt_mobile || "",
          email: data.contact_email || "",
          preferredContactTime: data.contact_time || "",
        });

        setEditingId(listing.id);
        setActiveTab("post");
        setStep(1);

        if (data.images && Array.isArray(data.images)) {
          setImages(data.images.map(img => ({
            previewUrl: img.image_url,
            file: null,
            existing: true,
            imageId: img.id
          })));
        } else {
          setImages([]);
        }
      }
    } catch (error) {
      console.error("Error fetching listing details:", error);
      toast.error("Failed to load property details");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({
      propertyTitle: "", listingType: "", propertyType: "", transactionType: "", expectedPrice: "", priceNegotiable: "Yes",
      city: "", locality: "", streetAddress: "", landmark: "", pincode: "", mapUrl: "",
      plotArea: "", builtupArea: "", bedrooms: "", bathrooms: "", balconies: "", furnishing: "", floorNumber: "", totalFloors: "", facing: "", carParking: "",
      amenities: [],
      description: "", highlights: "", nearbyLocations: "",
      ownerName: "", mobileNumber: "", altNumber: "", email: "", preferredContactTime: "",
    });
    setImages([]);
    setStep(1);
    navigate('/builder/housingplot');
  };

  const goNext = () => {
    if (!validateStep()) return;
    setStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
  };

  const goBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    setSubmitting(true);

    try {
      // Prepare form data
      const formData = new FormData();

      // Basic fields
      formData.append("listing_type", form.listingType);
      formData.append("property_category", "housing");
      formData.append("city", form.city);
      formData.append("locality", form.locality);
      formData.append("street_address", form.streetAddress);
      formData.append("landmark", form.landmark || "");
      formData.append("pincode", form.pincode || "");
      formData.append("google_map_link", form.mapUrl || "");

      if (form.latitude) formData.append("latitude", form.latitude);
      if (form.longitude) formData.append("longitude", form.longitude);

      // Contact details
      formData.append("contact_name", form.ownerName);
      formData.append("contact_mobile", form.mobileNumber);
      formData.append("contact_alt_mobile", form.altNumber || "");
      formData.append("contact_email", form.email);
      formData.append("contact_time", form.preferredContactTime || "");

      // Parse parking value to get numeric value
      let parkingSlots = 0;
      if (form.carParking) {
        if (form.carParking === "No Parking") {
          parkingSlots = 0;
        } else if (form.carParking === "1 Parking") {
          parkingSlots = 1;
        } else if (form.carParking === "2 Parking") {
          parkingSlots = 2;
        } else {
          const match = form.carParking.match(/\d+/);
          parkingSlots = match ? parseInt(match[0]) : 0;
        }
      }

      // Housing details
      const housingDetails = {
        property_type: form.propertyType,
        property_title: form.propertyTitle,
        transaction_type: form.transactionType,
        expected_price: form.expectedPrice,
        is_price_negotiable: form.priceNegotiable === "Yes" ? 1 : 0,
        plot_area: form.plotArea,
        built_up_area: form.builtupArea,
        bedrooms: form.bedrooms,
        bathrooms: form.bathrooms,
        balconies: form.balconies,
        furnishing_status: form.furnishing,
        floor_number: form.floorNumber,
        total_floors: form.totalFloors,
        facing_direction: form.facing,
        car_parking_slots: parkingSlots,
        amenities: form.amenities,
        description: form.description,
        highlights: form.highlights,
        nearby_locations: form.nearbyLocations
      };
      formData.append("housing_details", JSON.stringify(housingDetails));

      // Images
      if (deletedImageIds.length > 0) {
        formData.append("deleted_image_ids", JSON.stringify(deletedImageIds));
      }

      if (images.length > 0) {
        const primaryImg = images[0];
        if (primaryImg.existing && primaryImg.imageId) {
          formData.append("primary_image_id", primaryImg.imageId);
        } else {
          formData.append("new_primary_image_index", 0);
        }
      }

      images.forEach((img) => {
        if (img.file) {
          formData.append("images", img.file);
        }
      });

      // Submit to backend
      let response;
      if (editingId) {
        response = await axiosInstancev1.put(`/properties/${editingId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await axiosInstancev1.post("/properties", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (response.data.success) {
        toast.success(editingId ? "Property updated successfully!" : "Property listing created successfully!");
        if (editingId) {
          cancelEdit();
          setActiveTab("posted");
        } else {
          setCreatedListingId(response.data.listingId);
          setShowDurationModal(true);
        }
      }
    } catch (error) {
      console.error("Error submitting property:", error);
      toast.error(error.response?.data?.message || "Failed to create listing");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateOrder = (plan) => {
    // After successful payment, redirect or reload
    window.location.reload();
  };


  return (
    <>
      <div className="min-h-screen bg-white">
        <div className="w-full max-w-6xl mx-auto">
          {/* Internal Tab Navigation (Post/Posted/Enquiry) */}
          <div className="mb-4 border-b border-gray-200 overflow-x-auto no-scrollbar">
            <nav className="-mb-px flex space-x-4 sm:space-x-6 min-w-max" aria-label="Tabs">
              <button
                type="button"
                onClick={() => setActiveTab('post')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'post'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {listingMode === 'Rent' ? 'Post Rental' : 'Post House'}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('posted')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'posted'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Your Listings
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('enquiry')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'enquiry'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Enquiry Data
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'enquiry' ? (
            <CustomerEnquiries category="housing" />
          ) : activeTab === 'posted' ? (
            <PostedHouses onEdit={handleEdit} editBasePath={editBasePath || "/builder/real-estate"} listingTypeFilter={listingMode} />
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-[28px] border border-gray-200 shadow-sm px-5 py-5 sm:px-7 sm:py-7 lg:p-8 lg:rounded-3xl"
            >
              {/* Mobile header */}
              <div className="mb-4 lg:hidden"><GradientHeader step={step} totalSteps={TOTAL_STEPS} /></div>

              <div className="flex flex-col lg:flex-row lg:gap-10">
                {/* LEFT SIDEBAR – Desktop sticky */}
                <aside className="hidden lg:flex lg:flex-col lg:w-1/3 lg:pr-6 lg:border-r lg:border-gray-100 lg:sticky lg:top-6 lg:self-start">
                  <GradientHeader step={step} totalSteps={TOTAL_STEPS} />

                  <div className="mt-6 space-y-4">
                    <h1 className="text-xl font-semibold text-gray-900">
                      {STEPS_META.find((s) => s.id === step)?.title}
                    </h1>
                    <p className="text-sm text-gray-500">
                      {STEPS_META.find((s) => s.id === step)?.desc}
                    </p>
                  </div>

                  {/* Animated step indicators */}
                  <ul className="mt-6 space-y-2">
                    {STEPS_META.map((s) => {
                      const isActive = s.id === step;
                      const isCompleted = s.id < step;
                      return (
                        <li key={s.id}>
                          <button
                            type="button"
                            onClick={() => setStep(s.id)}
                            className={`w-full flex items-center gap-3 rounded-xl px-3 py-2 text-left transition-all duration-300
                            ${isActive
                                ? "bg-blue-50 text-blue-700 shadow-sm scale-[1.02]"
                                : isCompleted
                                  ? "bg-green-50 text-green-700"
                                  : "text-gray-600 hover:bg-gray-50"
                              }`}
                          >
                            <div className="flex items-center justify-center">
                              {isCompleted ? (
                                <CheckCircleIcon className="w-5 h-5 text-green-500" />
                              ) : (
                                <div
                                  className={`w-5 h-5 flex items-center justify-center rounded-full border text-[11px] font-semibold
                                ${isActive
                                      ? "border-blue-500 text-blue-600 bg-white"
                                      : "border-gray-300 text-gray-500 bg-white"
                                    }`}
                                >
                                  {s.id}
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="text-xs font-semibold">{s.label}</p>
                              <p className="text-[11px] text-gray-500 truncate">
                                {s.title}
                              </p>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </aside>

                {/* RIGHT CONTENT – Steps */}
                <div className="w-full lg:w-2/3 lg:pl-6 space-y-5">
                  {/* STEP 1 */}
                  {step === 1 && (
                    <StepCard
                      stepNumber={1}
                      title="Tell us about your place"
                      description="Start with the basics. You can edit everything later."
                      isOpen={openSections[1]}
                      toggle={() => setOpenSections(prev => ({ ...prev, 1: !prev[1] }))}
                    >
                      <div className="space-y-4">
                        {/* Property Title (Moved to Top) */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">
                            {listingMode === 'Rent' ? 'Property Title' : 'Property Title'}
                          </p>
                          <input
                            type="text"
                            className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                            placeholder={listingMode === 'Rent' ? "e.g. Spacious 2BHK for Rent" : "e.g. 3BHK Villa for Sale"}
                            value={form.propertyTitle}
                            onChange={(e) =>
                              updateField(
                                "propertyTitle",
                                e.target.value
                              )
                            }
                          />
                          {errors.propertyTitle && (
                            <p className="text-[11px] text-red-500 mt-1">
                              {errors.propertyTitle}
                            </p>
                          )}
                        </div>

                        {/* Description (Moved after Title) */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">
                            Description
                          </p>
                          <RichTextEditor
                            placeholder="Describe your property..."
                            value={form.description}
                            onChange={(content) => updateField("description", content)}
                            height={200}
                          />
                        </div>

                        {/* Transaction Type */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">
                            Transaction Type
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {TRANSACTION_TYPES.map((tt) => {
                              const active = form.transactionType === tt;
                              return (
                                <button
                                  type="button"
                                  key={tt}
                                  onClick={() =>
                                    updateField("transactionType", tt)
                                  }
                                  className={`px-3 py-1.5 rounded-full text-xs border transition-all duration-150
                            ${active
                                      ? "bg-blue-600 text-white border-blue-600"
                                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                                    }`}
                                >
                                  {tt}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Property Type */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">
                            Property Type
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {PROPERTY_TYPES.map((pt) => {
                              const active = form.propertyType === pt.id;
                              const Icon = pt.icon;
                              return (
                                <button
                                  type="button"
                                  key={pt.id}
                                  onClick={() =>
                                    updateField("propertyType", pt.id)
                                  }
                                  className={`flex flex-col items-center justify-center rounded-2xl border px-3 py-4 text-xs sm:text-sm transition-all duration-200
                            ${active
                                      ? "border-blue-500 bg-blue-50 text-blue-700 shadow-sm"
                                      : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100"
                                    }`}
                                >
                                  <Icon className="w-7 h-7 text-blue-600 mb-2" />
                                  <span className="font-medium text-center">
                                    {pt.label}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          {errors.propertyType && (
                            <p className="text-[11px] text-red-500 mt-1">
                              {errors.propertyType}
                            </p>
                          )}
                        </div>

                        {/* Listing Type */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">
                            Listing Type
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {LISTING_TYPES.map((lt) => {
                              // If listingMode is set, only show that mode
                              if (listingMode && listingMode !== lt) return null;

                              const active = form.listingType === lt;
                              return (
                                <button
                                  type="button"
                                  key={lt}
                                  onClick={() => updateField("listingType", lt)}
                                  disabled={!!listingMode} // Disable if listingMode is set
                                  className={`px-3 py-1.5 rounded-full text-xs border transition-all duration-150
                                    ${active
                                      ? "bg-blue-600 text-white border-blue-600"
                                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                                    }
                                    ${listingMode ? "opacity-50 cursor-not-allowed" : ""}
                                  `}
                                >
                                  {lt}
                                </button>
                              );
                            })}
                          </div>
                          {errors.listingType && (
                            <p className="text-[11px] text-red-500 mt-1">
                              {errors.listingType}
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                              <BanknotesIcon className="w-4 h-4 text-blue-600" />
                              Expected Price (₹)
                            </p>
                            <input
                              type="number"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              placeholder="e.g. 6500000"
                              value={form.expectedPrice}
                              onChange={(e) =>
                                updateField(
                                  "expectedPrice",
                                  e.target.value
                                )
                              }
                            />
                            {errors.expectedPrice && (
                              <p className="text-[11px] text-red-500 mt-1">
                                {errors.expectedPrice}
                              </p>
                            )}
                          </div>

                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">
                              Price Negotiable
                            </p>
                            <div className="flex gap-2">
                              {["Yes", "No"].map((opt) => {
                                const active =
                                  form.priceNegotiable === opt;
                                return (
                                  <button
                                    type="button"
                                    key={opt}
                                    onClick={() =>
                                      updateField(
                                        "priceNegotiable",
                                        opt
                                      )
                                    }
                                    className={`flex-1 px-3 py-2 rounded-2xl text-xs border transition-all duration-150
                                ${active
                                        ? "bg-green-50 border-green-500 text-green-700 shadow-[0_0_0_1px_rgba(22,163,74,0.15)]"
                                        : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                                      }`}
                                  >
                                    {opt}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </StepCard>
                  )}

                  {/* STEP 2 */}
                  {step === 2 && (
                    <StepCard
                      stepNumber={2}
                      title="Where is your property located?"
                      description="Exact location helps buyers find you easily."
                      isOpen={openSections[2]}
                      toggle={() => setOpenSections(prev => ({ ...prev, 2: !prev[2] }))}
                    >
                      <div className="space-y-3">
                        <div>
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                            <MapPinIcon className="w-4 h-4 text-blue-600" />
                            City
                          </p>
                          <LocationAutocomplete
                            value={form.city}
                            onChange={(val) => updateField("city", val)}
                            onSelect={(item) => updateField("city", item.city)}
                            mode="city"
                            placeholder="Enter city name..."
                            inputClassName="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                          />
                          {errors.city && (
                            <p className="text-[11px] text-red-500 mt-1">
                              {errors.city}
                            </p>
                          )}
                        </div>

                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">
                            Locality / Area
                          </p>
                          <LocationAutocomplete
                            value={form.locality}
                            onChange={(val) => updateField("locality", val)}
                            onSelect={(item) =>
                              updateField("locality", item.locality || item.label)
                            }
                            mode="locality"
                            cityFilter={form.city || null}
                            placeholder="Enter locality / area..."
                            inputClassName="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                          />
                          {errors.locality && (
                            <p className="text-[11px] text-red-500 mt-1">
                              {errors.locality}
                            </p>
                          )}
                        </div>

                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">
                            Street Address
                          </p>
                          <input
                            type="text"
                            className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                            value={form.streetAddress}
                            onChange={(e) =>
                              updateField(
                                "streetAddress",
                                e.target.value
                              )
                            }
                          />
                          {errors.streetAddress && (
                            <p className="text-[11px] text-red-500 mt-1">
                              {errors.streetAddress}
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">
                              Landmark (optional)
                            </p>
                            <input
                              type="text"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.landmark}
                              onChange={(e) =>
                                updateField(
                                  "landmark",
                                  e.target.value
                                )
                              }
                            />
                          </div>

                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">
                              Pincode
                            </p>
                            <input
                              type="text"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.pincode}
                              onChange={(e) =>
                                updateField(
                                  "pincode",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>

                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">
                            Google Map Link (optional)
                          </p>
                          <input
                            type="text"
                            className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                            placeholder="Paste Google Maps URL"
                            value={form.mapUrl}
                            onChange={(e) =>
                              updateField("mapUrl", e.target.value)
                            }
                          />
                        </div>

                        {/* Interactive Location Picker */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">
                            📍 Pin Property Location on Map
                          </p>
                          <p className="text-xs text-gray-500 mb-3">
                            Use GPS or click on the map to mark your property location. This helps buyers find properties in your area.
                          </p>
                          <LocationPicker
                            onLocationSelect={({ latitude, longitude }) => {
                              updateField("latitude", latitude);
                              updateField("longitude", longitude);
                            }}
                            initialLat={form.latitude || 8.7139}
                            initialLng={form.longitude || 77.7567}
                          />
                          {form.latitude && form.longitude && (
                            <div className="mt-2 text-xs text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                              ✓ Location saved: {parseFloat(form.latitude).toFixed(6)}, {parseFloat(form.longitude).toFixed(6)}
                            </div>
                          )}
                        </div>
                      </div>
                    </StepCard>
                  )}

                  {/* STEP 3 */}
                  {step === 3 && (
                    <StepCard
                      stepNumber={3}
                      title="How is your property structured?"
                      description="Share key details to help buyers understand the space."
                      isOpen={openSections[3]}
                      toggle={() => setOpenSections(prev => ({ ...prev, 3: !prev[3] }))}
                    >
                      <div className="space-y-5">
                        {/* Row 1 */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                              <ArrowsPointingOutIcon className="w-4 h-4 text-blue-600" />
                              Plot Area (sq ft)
                            </p>
                            <input
                              type="number"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.plotArea}
                              onChange={(e) =>
                                updateField("plotArea", e.target.value)
                              }
                            />
                            {errors.plotArea && (
                              <p className="text-[11px] text-red-500 mt-1">
                                {errors.plotArea}
                              </p>
                            )}
                          </div>

                          <div>
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                              <Squares2X2Icon className="w-4 h-4 text-blue-600" />
                              Built-up Area (sq ft)
                            </p>
                            <input
                              type="number"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.builtupArea}
                              onChange={(e) =>
                                updateField(
                                  "builtupArea",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>

                        {/* Row 2 */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                              <UserGroupIcon className="w-4 h-4 text-blue-600" />
                              Bedrooms
                            </p>
                            <input
                              type="number"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.bedrooms}
                              onChange={(e) =>
                                updateField("bedrooms", e.target.value)
                              }
                            />
                            {errors.bedrooms && (
                              <p className="text-[11px] text-red-500 mt-1">
                                {errors.bedrooms}
                              </p>
                            )}
                          </div>

                          <div>
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                              <SparklesIcon className="w-4 h-4 text-blue-600" />
                              Bathrooms
                            </p>
                            <input
                              type="number"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.bathrooms}
                              onChange={(e) =>
                                updateField(
                                  "bathrooms",
                                  e.target.value
                                )
                              }
                            />
                            {errors.bathrooms && (
                              <p className="text-[11px] text-red-500 mt-1">
                                {errors.bathrooms}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Row 3 */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">
                              Balconies
                            </p>
                            <input
                              type="number"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.balconies}
                              onChange={(e) =>
                                updateField(
                                  "balconies",
                                  e.target.value
                                )
                              }
                            />
                          </div>

                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">
                              Floor No.
                            </p>
                            <input
                              type="number"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.floorNumber}
                              onChange={(e) =>
                                updateField(
                                  "floorNumber",
                                  e.target.value
                                )
                              }
                            />
                          </div>

                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">
                              Total Floors
                            </p>
                            <input
                              type="number"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.totalFloors}
                              onChange={(e) =>
                                updateField(
                                  "totalFloors",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>

                        {/* Furnishing */}
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-gray-700">
                            Furnishing
                          </p>

                          <div className="flex flex-wrap gap-2">
                            {FURNISHING.map((f) => {
                              const active = form.furnishing === f;
                              return (
                                <button
                                  type="button"
                                  key={f}
                                  onClick={() =>
                                    updateField("furnishing", f)
                                  }
                                  className={`px-3 py-1.5 rounded-full text-xs border transition-all duration-150
                ${active
                                      ? "bg-blue-600 text-white border-blue-600 shadow-[0_0_0_1px_rgba(37,99,235,0.35)]"
                                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                                    }`}
                                >
                                  {f}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Facing */}
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-gray-700">
                            Facing
                          </p>

                          <div className="flex flex-wrap gap-2">
                            {FACING.map((f) => {
                              const active = form.facing === f;
                              return (
                                <button
                                  type="button"
                                  key={f}
                                  onClick={() =>
                                    updateField("facing", f)
                                  }
                                  className={`px-3 py-1.5 rounded-full text-xs border transition-all duration-150
                ${active
                                      ? "bg-blue-600 text-white border-blue-600 shadow-[0_0_0_1px_rgba(37,99,235,0.35)]"
                                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                                    }`}
                                >
                                  {f}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Parking */}
                        <div className="space-y-2">
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                            <TruckIcon className="w-4 h-4 text-blue-600" />
                            Car Parking
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {PARKING.map((p) => {
                              const active = form.carParking === p;
                              return (
                                <button
                                  type="button"
                                  key={p}
                                  onClick={() =>
                                    updateField("carParking", p)
                                  }
                                  className={`px-3 py-1.5 rounded-full text-xs border transition-all duration-150
                ${active
                                      ? "bg-blue-600 text-white border-blue-600 shadow-[0_0_0_1px_rgba(37,99,235,0.35)]"
                                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                                    }`}
                                >
                                  {p}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </StepCard>
                  )}

                  {/* STEP 4 */}
                  {step === 4 && (
                    <StepCard
                      stepNumber={4}
                      title="Make your listing more attractive"
                      description="Add amenities and photos to stand out."
                      isOpen={openSections[4]}
                      toggle={() => setOpenSections(prev => ({ ...prev, 4: !prev[4] }))}
                    >
                      <div className="space-y-5">
                        {/* Amenities */}
                        <div>
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                            <ShieldCheckIcon className="w-4 h-4 text-blue-600" />
                            Amenities
                          </p>
                          <p className="text-[11px] text-gray-500 mb-2">
                            Tap to select all facilities available in this
                            property.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {AMENITIES.map((a) => {
                              const active =
                                form.amenities.includes(a);
                              return (
                                <button
                                  type="button"
                                  key={a}
                                  onClick={() =>
                                    toggleAmenity(a)
                                  }
                                  className={`px-3 py-1.5 rounded-full text-[11px] border flex items-center gap-1.5 transition-all duration-150
                            ${active
                                      ? "bg-blue-600 text-white border-blue-600 shadow-[0_0_0_1px_rgba(37,99,235,0.35)]"
                                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                                    }`}
                                >
                                  <BoltIcon className="w-3 h-3" />
                                  {a}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Upload + Preview grid for desktop */}
                        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr,1fr] gap-4 lg:gap-6 items-start">
                          {/* Upload area */}
                          <div>
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                              <PhotoIcon className="w-4 h-4 text-blue-600" />
                              Property Photos
                            </p>
                            <p className="text-[11px] text-gray-500 mb-2">
                              Add bright, clear photos: living room,
                              bedrooms, kitchen, bathrooms, balcony, and
                              floor plan.
                            </p>

                            <label className="block">
                              <div className="w-full rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50/60 px-4 py-6 text-center text-xs text-blue-700 cursor-pointer hover:bg-blue-50 transition-all duration-150 hover:shadow-[0_0_0_1px_rgba(37,99,235,0.25)]">
                                <p className="font-medium mb-1">
                                  Tap to upload or drag & drop
                                </p>
                                <p>JPEG, PNG — multiple files allowed</p>
                              </div>
                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                className="hidden"
                                onChange={handleImages}
                              />
                            </label>
                          </div>

                          {/* Right-side gallery preview */}
                          <div className="hidden lg:block">
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">
                              Live Preview
                            </p>
                            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-2 h-[150px] overflow-hidden">
                              {images.length === 0 ? (
                                <div className="w-full h-full flex items-center justify-center text-[11px] text-gray-400 text-center px-4">
                                  Uploaded images will appear here as a
                                  quick preview grid.
                                </div>
                              ) : (
                                <div className="grid grid-cols-3 gap-1.5">
                                  {images.slice(0, 6).map((img, i) => (
                                    <div
                                      key={i}
                                      onClick={() => setAsMain(i)}
                                      className={`relative overflow-hidden rounded-xl group border-2 cursor-pointer transition-all duration-200 ${i === 0 ? "border-blue-600 shadow-md scale-[1.02]" : "border-gray-100 hover:border-blue-400"}`}
                                      title={i === 0 ? "Banner Image" : "Click to set as Banner"}
                                    >
                                      <img
                                        src={img.previewUrl}
                                        alt={`Preview ${i + 1}`}
                                        className="w-full h-14 object-cover"
                                      />

                                      {/* Banner Badge */}
                                      {i === 0 ? (
                                        <div className="absolute top-0.5 left-0.5 bg-blue-600 text-[8px] text-white px-2 py-0.5 rounded-full font-bold shadow-sm z-10">
                                          Banner
                                        </div>
                                      ) : (
                                        <div className="absolute top-0.5 left-0.5 bg-white/90 text-blue-600 text-[7px] px-1.5 py-0.5 rounded-full font-bold opacity-0 group-hover:opacity-100 transition-opacity z-10 border border-blue-100">
                                          Set Banner
                                        </div>
                                      )}

                                      <div className="absolute top-1 right-1 flex flex-col gap-1 z-20">
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            removeMedia(i);
                                          }}
                                          className="bg-red-500/90 text-white rounded-full p-1 shadow-sm hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                                          title="Remove image"
                                        >
                                          <TrashIcon className="w-3 h-3" />
                                        </button>

                                        {i !== 0 && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setAsMain(i);
                                            }}
                                            className="bg-blue-600/90 text-white rounded-full p-1 shadow-sm hover:bg-blue-700 transition-colors lg:opacity-0 lg:group-hover:opacity-100"
                                            title="Set as Banner image"
                                          >
                                            <CheckCircleIcon className="w-3 h-3" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Mobile / tablet preview under upload */}
                        {images.length > 0 && (
                          <div className="mt-2 grid grid-cols-3 gap-2 lg:hidden">
                            {images.map((img, i) => (
                              <div
                                key={i}
                                onClick={() => setAsMain(i)}
                                className={`relative overflow-hidden rounded-2xl border-2 active:scale-95 transition-all duration-200 ${i === 0 ? "border-blue-500 shadow-sm" : "border-gray-100"}`}
                              >
                                <img
                                  src={img.previewUrl}
                                  alt={`Property ${i + 1}`}
                                  className="w-full h-20 object-cover"
                                />

                                {/* Banner Badge */}
                                {i === 0 ? (
                                  <div className="absolute top-1 left-1 bg-blue-600 text-[10px] text-white px-2 py-0.5 rounded-full font-bold shadow-sm">
                                    Banner
                                  </div>
                                ) : (
                                  <div className="absolute top-1 left-1 bg-white/90 text-blue-600 text-[9px] px-2 py-0.5 rounded-full font-bold border border-blue-100 shadow-sm">
                                    Set Banner
                                  </div>
                                )}

                                <div className="absolute top-1 right-1 flex flex-col gap-2">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeMedia(i);
                                    }}
                                    className="bg-red-500 text-white rounded-full p-1.5 shadow-sm active:bg-red-600"
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                  </button>

                                  {i !== 0 && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setAsMain(i);
                                      }}
                                      className="bg-blue-600 text-white rounded-full p-1.5 shadow-sm active:bg-blue-700"
                                    >
                                      <CheckCircleIcon className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Text sections */}
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">
                              Highlights
                            </p>
                            <RichTextEditor
                              placeholder="Corner plot, park-facing, premium gated community, etc."
                              value={form.highlights}
                              onChange={(content) => updateField("highlights", content)}
                              height={150}
                            />
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">
                              Nearby Locations
                            </p>
                            <RichTextEditor
                              placeholder="Schools, hospitals, bus stops, metro, IT parks..."
                              value={form.nearbyLocations}
                              onChange={(content) => updateField("nearbyLocations", content)}
                              height={150}
                            />
                          </div>
                        </div>
                      </div>
                    </StepCard>
                  )}

                  {/* STEP 5 */}
                  {step === 5 && (
                    <StepCard
                      stepNumber={5}
                      title="How can buyers contact you?"
                      description="Your contact details are shared only with interested buyers."
                      isOpen={openSections[5]}
                      toggle={() => setOpenSections(prev => ({ ...prev, 5: !prev[5] }))}
                    >
                      <div className="space-y-4">
                        {/* Quick preview card */}
                        <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-700 space-y-1">
                          <p className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
                            <HomeIcon className="w-4 h-4 text-blue-600" />
                            {form.propertyTitle || "Untitled Property"}
                          </p>
                          <p>
                            {form.city && form.locality
                              ? `${form.locality}, ${form.city}`
                              : "Location not fully filled yet"}
                          </p>
                          <p>
                            {form.expectedPrice
                              ? `₹${form.expectedPrice}`
                              : "Price not set"}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            {form.bedrooms && form.bathrooms
                              ? `${form.bedrooms} BHK • ${form.bathrooms} Bath • ${form.plotArea || "NA"
                              } sq ft`
                              : "Add details in previous steps if needed"}
                          </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                              <UserIcon className="w-4 h-4 text-blue-600" />
                              Owner Name
                            </p>
                            <input
                              type="text"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.ownerName}
                              onChange={(e) =>
                                updateField(
                                  "ownerName",
                                  e.target.value
                                )
                              }
                            />
                            {errors.ownerName && (
                              <p className="text-[11px] text-red-500 mt-1">
                                {errors.ownerName}
                              </p>
                            )}
                          </div>
                          <div>
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                              <PhoneIcon className="w-4 h-4 text-blue-600" />
                              Mobile Number
                            </p>
                            <input
                              type="text"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.mobileNumber}
                              onChange={(e) =>
                                updateField(
                                  "mobileNumber",
                                  e.target.value
                                )
                              }
                            />
                            {errors.mobileNumber && (
                              <p className="text-[11px] text-red-500 mt-1">
                                {errors.mobileNumber}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">
                              Alternate Number
                            </p>
                            <input
                              type="text"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.altNumber}
                              onChange={(e) =>
                                updateField(
                                  "altNumber",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                          <div>
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                              <EnvelopeIcon className="w-4 h-4 text-blue-600" />
                              Email
                            </p>
                            <input
                              type="email"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.email}
                              onChange={(e) =>
                                updateField("email", e.target.value)
                              }
                            />
                            {errors.email && (
                              <p className="text-[11px] text-red-500 mt-1">
                                {errors.email}
                              </p>
                            )}
                          </div>
                        </div>

                        <div>
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                            <ClockIcon className="w-4 h-4 text-blue-600" />
                            Preferred Contact Time
                          </p>
                          <input
                            type="text"
                            className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                            placeholder="e.g. 10 AM – 7 PM"
                            value={form.preferredContactTime}
                            onChange={(e) =>
                              updateField(
                                "preferredContactTime",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                    </StepCard>
                  )}

                  {/* NAV BAR */}
                  <div className="mt-4 flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={goBack}
                      disabled={step === 1}
                      className={`text-xs font-medium px-4 py-2 rounded-full border transition
                  ${step === 1
                          ? "border-gray-100 text-gray-300 cursor-not-allowed"
                          : "border-gray-200 text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                      Back
                    </button>

                    {step < TOTAL_STEPS ? (
                      <button
                        type="button"
                        onClick={goNext}
                        className="ml-auto text-xs sm:text-sm font-semibold px-5 py-2.5 rounded-full bg-blue-600 text-white shadow-sm hover:bg-blue-700 transition"
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={submitting}
                        className="ml-auto text-xs sm:text-sm font-semibold px-6 py-2.5 rounded-full bg-green-600 text-white shadow-sm hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {submitting ? "Publishing..." : "Publish Listing"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div >
      {showDurationModal && (
        <DurationSelectionModal
          isOpen={showDurationModal}
          onClose={() => setShowDurationModal(false)}
          listingId={createdListingId}
          onSuccess={handleCreateOrder}
          planType="HOUSING"
        />
      )
      }
    </>
  );
};

export default HousingPlot;
